import { useCallback } from "react";

import type { Exact, PriceRankItem, RoomPriceRankGetNewPriceRankItemQuery } from "@/lib/gql/__generated__/graphql";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { useRoomPrice } from "./composition/use-room-price";
import { useRoomPriceRankListItem } from "./composition/use-room-price-rank-list-item";
import { useSelectedPriceRank } from "./composition/use-selected-price-rank";

import type { ProductRoomPriceRankFormModel } from "../../../../model/price-rank-and-daily-status/_shared/price-rank/type";
import type { ProductRoomPriceRankOnSetViewModelType } from "../../../../model/price-rank-and-daily-status/_shared/price-rank/use-case/use-room-price-rank-model";
import type { RoomPriceRankItemType } from "../type";
// eslint-disable-next-line import/no-restricted-paths
import type { ApolloQueryResult } from "@apollo/client";

type Props = {
	roomPriceRankMast: ProductRoomPriceRankFormModel["ViewModel"];
	onSetRoomPriceRankMast: ProductRoomPriceRankOnSetViewModelType;

	fetchNewPriceRankItem: (
		variables?:
			| Partial<
					Exact<{
						hotelID: string;
					}>
			  >
			| undefined,
	) => Promise<ApolloQueryResult<RoomPriceRankGetNewPriceRankItemQuery | undefined>>;

	onSetSelectedPriceRank: (input: PriceRankItem) => void;
};

export const useRoomPriceRankList = ({
	roomPriceRankMast,
	onSetRoomPriceRankMast,
	fetchNewPriceRankItem,
	onSetSelectedPriceRank: _onSetSelectedPriceRank, //
}: Props) => {
	const { hotelID } = roomPriceRankMast;

	const onSetRoomPriceRankItem = useCallback(
		(index: number) => (value: RoomPriceRankItemType) => {
			const newItems = roomPriceRankMast.ranks.value.map((rank, idx) => {
				if (idx === index) {
					return value;
				} else {
					return rank;
				}
			});

			onSetRoomPriceRankMast([
				{
					key: "ranks",
					value: {
						isEdited: true,
						hasError: false,
						value: newItems,
					},
				},
			]);
		},
		[onSetRoomPriceRankMast, roomPriceRankMast.ranks],
	);

	const appendPriceRank = useCallback(async () => {
		const newItem = await fetchNewPriceRankItem({ hotelID });

		if (!newItem.data?.roomPriceRankGetNewPriceRankItem) {
			notifyUserEventInfo({
				variant: "error",
				title: "新しい料金ランクの取得に失敗しました",
			});

			return;
		}

		const { roomPriceRankGetNewPriceRankItem } = newItem.data;

		const convertedNewItem = {
			rooms: {
				isEdited: true,
				hasError: false,
				value: roomPriceRankGetNewPriceRankItem.rooms?.map((room) => {
					return {
						roomID: {
							isEdited: true,
							value: room.roomID,
						},
						roomName: {
							isEdited: true,
							value: room.roomName,
						},
						prices: {
							isEdited: true,
							hasError: false,
							value: room.prices?.map((price) => {
								return {
									adultNum: {
										isEdited: true,
										value: price.adultNum,
									},
									price: {
										amount: {
											isEdited: true,
											value: price.price.amount,
										},
										currencyType: {
											isEdited: true,
											value: price.price.currencyType,
										},
									},
								};
							}),
						},
					};
				}),
			},
			name: {
				isEdited: true,
				value: roomPriceRankGetNewPriceRankItem.name,
			},
			color: {
				isEdited: true,
				value: roomPriceRankGetNewPriceRankItem.color,
			},
			priceRankID: {
				isEdited: true,
				value: roomPriceRankGetNewPriceRankItem.priceRankID,
			},
			isStopSelling: {
				isEdited: true,
				value: roomPriceRankGetNewPriceRankItem.isStopSelling || false,
			},
		} satisfies RoomPriceRankItemType;

		onSetRoomPriceRankMast([
			{
				key: "ranks",
				value: {
					isEdited: true,
					hasError: false,
					value: [...roomPriceRankMast.ranks.value, convertedNewItem],
				},
			},
		]);

		notifyUserEventInfo({
			title: "新しい料金ランクを追加しました",
		});
	}, [fetchNewPriceRankItem, hotelID, onSetRoomPriceRankMast, roomPriceRankMast.ranks.value]);

	const removePriceRank = useCallback(
		(idx: number) => () => {
			const newItems = roomPriceRankMast.ranks.value.filter((_, i) => i !== idx);

			onSetRoomPriceRankMast([
				{
					key: "ranks",
					value: {
						isEdited: true,
						hasError: false,
						value: newItems,
					},
				},
			]);
		},
		[onSetRoomPriceRankMast, roomPriceRankMast.ranks],
	);

	return {
		appendPriceRank,
		removePriceRank,

		...useRoomPriceRankListItem({
			roomPriceRankMast,
			onSetRoomPriceRankMast,
			onSetSelectedPriceRank: _onSetSelectedPriceRank,
		}),

		...useSelectedPriceRank({ onSetSelectedPriceRank: _onSetSelectedPriceRank }),

		...useRoomPrice({
			onSetRoomPriceRankItem,
			onSetSelectedPriceRank: _onSetSelectedPriceRank,
		}),

		onSetRoomPriceRankItem,
	};
};
